/**
 * claimJobTransaction
 *
 * Pure transactional helper for the "Take the Job" action used in
 * `src/pages/ShiftDetails.tsx`. Extracted so the race-condition logic
 * can be unit-tested independently of Firestore and the React component.
 *
 * Contract:
 *   - Reads master job doc + the werker's own job doc inside a Firestore
 *     transaction (optimistic concurrency).
 *   - Throws with a user-facing message if the werker can't take the job.
 *   - Otherwise increments scheduledCount, flips master to Filled when full,
 *     and marks the werker's doc as Scheduled.
 *   - Returns { notifyScheduler } so the caller knows whether to fire the
 *     "job filled" notification after the transaction commits.
 */

import { WORKFLOW_WERKER_TYPES, WORKFLOW_SCHEDULER_TYPES } from "./postActionTypes";

// Minimal structural types so this module doesn't take a hard dependency
// on the Firestore SDK shape (keeps it trivially mockable in tests).
export interface ClaimTransactionLike {
    get(ref: any): Promise<{ exists: () => boolean; data: () => any }>;
    update(ref: any, data: Record<string, any>): void;
}

export interface ClaimJobTransactionRefs {
    jobDocRef: any;
    myJobDocRef: any;
}

export interface ClaimJobTransactionResult {
    notifyScheduler: boolean;
}

export async function claimJobTransaction(
    transaction: ClaimTransactionLike,
    { jobDocRef, myJobDocRef }: ClaimJobTransactionRefs,
): Promise<ClaimJobTransactionResult> {
    const [jobSnap, myJobSnap] = await Promise.all([
        transaction.get(jobDocRef),
        transaction.get(myJobDocRef),
    ]);

    if (!jobSnap.exists()) {
        throw new Error("Job no longer exists.");
    }
    if (!myJobSnap.exists()) {
        throw new Error("Your job record is missing. Please refresh.");
    }

    const myJobData: any = myJobSnap.data();
    const currentWerkerStatus = myJobData.jobInfo.status.jobStatusWerker;

    if (currentWerkerStatus === WORKFLOW_WERKER_TYPES.Scheduled) {
        throw new Error("You already took this job.");
    }
    if (currentWerkerStatus === WORKFLOW_WERKER_TYPES.Past) {
        throw new Error("This job has already passed.");
    }

    const jobData: any = jobSnap.data();
    const totalSlots = jobData.jobInfo.core.numJobSlots;

    const scheduledCountRaw = jobData.jobInfo.status.scheduledCount;
    const scheduledCount =
        typeof scheduledCountRaw === "number" && Number.isFinite(scheduledCountRaw)
            ? scheduledCountRaw
            : 0;

    if (scheduledCount >= totalSlots) {
        throw new Error("Dang... just missed it.");
    }

    const newCount = scheduledCount + 1;

    transaction.update(jobDocRef, {
        "jobInfo.status.scheduledCount": newCount,
        ...(newCount >= totalSlots && {
            "jobInfo.status.jobStatusSched": WORKFLOW_SCHEDULER_TYPES.Filled,
        }),
    });

    transaction.update(myJobDocRef, {
        "jobInfo.status.jobStatusWerker": WORKFLOW_WERKER_TYPES.Scheduled,
        "docStamp.timestamp.updatedAt": Date.now(),
    });

    return { notifyScheduler: newCount >= totalSlots };
}
